import {
  APP_AWAY_EVENT,
  subscribeAppResume,
} from "./appResumeCoordinator";

// Stale-client self-heal — INCIDENT-002. A manifest mismatch no longer hard
// reloads an authenticated working surface: ordinary unsaved form memory is not
// covered by C1 persistence. Instead the guard publishes an update-ready signal.
// App owns the visible choice and the exact pre-reload persistence barrier.

const MANIFEST_URL = "/asset-manifest.json";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MANIFEST_TIMEOUT_MS = 10_000;
export const STALE_BUILD_EVENT = "lnpro:stale-build-available";

export interface StaleBuildSignal {
  startupSignature: string;
  targetSignature: string;
  detectedAt: number;
}

export type StaleBuildRevalidation =
  | "confirmed"
  | "changed"
  | "current"
  | "unreachable";

type StaleBuildListener = (signal: StaleBuildSignal) => void;

let startupSignature: string | null = null;
let pendingTargetSignature: string | null = null;
let checkInFlight = false;
let timer: ReturnType<typeof setInterval> | null = null;
let startPromise: Promise<void> | null = null;
let unsubscribeResume: (() => void) | null = null;
let lifecycleEpoch = 0;
let activeController: AbortController | null = null;
const listeners = new Set<StaleBuildListener>();

/** Entrypoint filenames, order-normalised, as one comparable string. */
function signatureOf(manifest: unknown): string | null {
  if (!manifest || typeof manifest !== "object") return null;
  const entrypoints = (manifest as { entrypoints?: unknown }).entrypoints;
  if (!Array.isArray(entrypoints) || entrypoints.length === 0) return null;
  const names = entrypoints.filter((entry): entry is string => typeof entry === "string");
  if (names.length === 0) return null;
  return [...names].sort().join("|");
}

async function fetchSignature(): Promise<string | null> {
  const controller = new AbortController();
  activeController = controller;
  const timeout = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return signatureOf(await response.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
    if (activeController === controller) activeController = null;
  }
}

const publishStaleBuild = (targetSignature: string) => {
  if (!startupSignature || pendingTargetSignature === targetSignature) return;
  pendingTargetSignature = targetSignature;
  const signal: StaleBuildSignal = {
    startupSignature,
    targetSignature,
    detectedAt: Date.now(),
  };
  listeners.forEach((listener) => {
    try {
      listener(signal);
    } catch {
      // An observer cannot prevent the operator from seeing the update.
    }
  });
  window.dispatchEvent(
    new CustomEvent<StaleBuildSignal>(STALE_BUILD_EVENT, { detail: signal })
  );
};

async function checkForStaleClient(): Promise<void> {
  if (checkInFlight || document.visibilityState !== "visible") return;
  checkInFlight = true;
  try {
    const current = await fetchSignature();
    if (current === null) return;
    if (startupSignature === null) {
      // Startup may have been offline. Establish a retryable baseline rather
      // than permanently disabling the guard.
      startupSignature = current;
      return;
    }
    if (current === startupSignature) {
      pendingTargetSignature = null;
      return;
    }
    publishStaleBuild(current);
  } finally {
    checkInFlight = false;
  }
}

/** Start once; startup failure remains retryable on the next foreground signal. */
export async function startStaleClientGuard(): Promise<void> {
  if (process.env.NODE_ENV !== "production") return;
  if (timer !== null) return startPromise ?? Promise.resolve();

  const epoch = ++lifecycleEpoch;
  unsubscribeResume = subscribeAppResume(() => {
    void checkForStaleClient();
  });
  const onAway = () => activeController?.abort();
  window.addEventListener(APP_AWAY_EVENT, onAway);
  awayCleanup = () => window.removeEventListener(APP_AWAY_EVENT, onAway);
  timer = setInterval(() => {
    if (document.visibilityState === "visible") void checkForStaleClient();
  }, CHECK_INTERVAL_MS);

  startPromise = (async () => {
    const signature = await fetchSignature();
    if (epoch === lifecycleEpoch) startupSignature = signature;
  })();
  try {
    await startPromise;
  } finally {
    if (epoch === lifecycleEpoch) startPromise = null;
  }
}

let awayCleanup: (() => void) | null = null;

export function subscribeStaleBuild(listener: StaleBuildListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Recheck the exact target immediately before the operator-approved reload. */
export async function revalidateStaleBuildTarget(
  targetSignature: string
): Promise<StaleBuildRevalidation> {
  const current = await fetchSignature();
  if (current === null) return "unreachable";
  if (current === targetSignature && current !== startupSignature) return "confirmed";
  if (current === startupSignature) return "current";
  publishStaleBuild(current);
  return "changed";
}

export function stopStaleClientGuard(): void {
  lifecycleEpoch += 1;
  activeController?.abort();
  activeController = null;
  if (timer !== null) clearInterval(timer);
  timer = null;
  unsubscribeResume?.();
  unsubscribeResume = null;
  awayCleanup?.();
  awayCleanup = null;
  startupSignature = null;
  pendingTargetSignature = null;
  checkInFlight = false;
  startPromise = null;
}

export const __testing = {
  signatureOf,
  checkForStaleClient,
  reset() {
    stopStaleClientGuard();
    listeners.clear();
  },
};
